import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getDefaultActiveRole } from "../features/auth/roleContext";

export type SessionUser = {
  id: string;
  username: string;
  displayName: string;
  roles: string[];
  permissions: string[];
  avatarUrl?: string;
};

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresOnUtc: string | null;
  refreshTokenExpiresOnUtc: string | null;
  tenantId: string | null;
  branchId: string | null;
  branchName: string | null;
  homeBranchId: string | null;
  homeBranchName: string | null;
  activeRole: string | null;
  user: SessionUser | null;
  setSession: (payload: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresOnUtc?: string | null;
    refreshTokenExpiresOnUtc?: string | null;
    tenantId: string;
    branchId: string;
    branchName?: string | null;
    activeRole?: string | null;
    user: SessionUser;
  }) => void;
  setTokens: (payload: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresOnUtc?: string | null;
    refreshTokenExpiresOnUtc?: string | null;
  }) => void;
  setBranchSwitchContext: (payload: {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresOnUtc?: string | null;
    refreshTokenExpiresOnUtc?: string | null;
    branchId: string;
    branchName?: string | null;
    role: string;
  }) => void;
  setActiveRole: (activeRole: string) => void;
  setUser: (user: SessionUser | null) => void;
  clearSession: () => void;
  isMainBranchAdmin: () => boolean;
  isInSwitchedBranchContext: () => boolean;
};

const initialState = {
  accessToken: null,
  refreshToken: null,
  accessTokenExpiresOnUtc: null,
  refreshTokenExpiresOnUtc: null,
  tenantId: null,
  branchId: null,
  branchName: null,
  homeBranchId: null,
  homeBranchName: null,
  activeRole: null,
  user: null
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSession: (payload) =>
        set({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          accessTokenExpiresOnUtc: payload.accessTokenExpiresOnUtc ?? null,
          refreshTokenExpiresOnUtc: payload.refreshTokenExpiresOnUtc ?? null,
          tenantId: payload.tenantId,
          branchId: payload.branchId,
          branchName: payload.branchName ?? null,
          homeBranchId: payload.branchId,
          homeBranchName: payload.branchName ?? null,
          activeRole: payload.activeRole ?? getDefaultActiveRole(payload.user.roles),
          user: payload.user
        }),

      setTokens: (payload) =>
        set({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          accessTokenExpiresOnUtc: payload.accessTokenExpiresOnUtc ?? null,
          refreshTokenExpiresOnUtc: payload.refreshTokenExpiresOnUtc ?? null
        }),

      setBranchSwitchContext: (payload) =>
        set({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          accessTokenExpiresOnUtc: payload.accessTokenExpiresOnUtc ?? null,
          refreshTokenExpiresOnUtc: payload.refreshTokenExpiresOnUtc ?? null,
          branchId: payload.branchId,
          branchName: payload.branchName ?? null,
          activeRole: payload.role
        }),

      setActiveRole: (activeRole) =>
        set((state) => {
          if (!state.user) return { activeRole: null };
          const roleExists = state.user.roles.some((role) => role.toLowerCase() === activeRole.toLowerCase());
          if (!roleExists) return { activeRole: state.activeRole };
          return { activeRole };
        }),

      setUser: (user) =>
        set((state) => {
          if (!user) return { user: null, activeRole: null };
          const stillValidActiveRole =
            state.activeRole !== null &&
            user.roles.some((role) => role.toLowerCase() === state.activeRole?.toLowerCase());

          return {
            user,
            activeRole: stillValidActiveRole ? state.activeRole : getDefaultActiveRole(user.roles)
          };
        }),

      clearSession: () => set(initialState),

      isMainBranchAdmin: () => {
        const { homeBranchId, user } = get();
        if (!user || !homeBranchId) return false;
        const isSuperAdmin = user.roles.some((r) => r.toLowerCase() === "superadmin");
        if (isSuperAdmin) return false;
        return homeBranchId.toLowerCase() === "main" && user.roles.some((r) => r.toLowerCase() === "administrator");
      },

      isInSwitchedBranchContext: () => {
        const { branchId, homeBranchId } = get();
        if (!branchId || !homeBranchId) return false;
        return branchId.toLowerCase() !== homeBranchId.toLowerCase();
      }
    }),
    {
      name: "healthcare-ui-auth-session"
    }
  )
);
